import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Sun,
  UtensilsCrossed,
  Moon,
  Cookie,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { CalendarDateRangePicker } from '../../components/CalendarDatePicker';
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import {
  nutritionService,
  NutritionChartDataPoint,
  NutritionDataResponse,
  MealLoggingStatsResponse,
} from '../../services/nutrition/nutritionService';
import { MealPlanSummary, MealPlanVersionResponse, MealPlanDay } from '../../services/nutrition/mealPlanService';
import { DEFAULT_MEAL_TYPE_ORDER } from '../../services/nutrition/mealTypeConstants';
import './MealPlanMonitoringTab.css';

/* Recharts polar components return ReactNode; cast to any for React 19 JSX compatibility */
const PolarAngleAxisBox = PolarAngleAxis as React.ComponentType<Record<string, unknown>>;
const PolarRadiusAxisBox = PolarRadiusAxis as React.ComponentType<Record<string, unknown>>;
const PolarGridBox = PolarGrid as React.ComponentType<Record<string, unknown>>;
const RadarBox = Radar as React.ComponentType<Record<string, unknown>>;
const RadarChartBox = RadarChart as React.ComponentType<Record<string, unknown>>;

/**
 * Meal Plan Monitoring tab.
 * Intentionally does NOT include: Export Report button, Clinical Insight section.
 *
 * Metrics we currently compute and display:
 * - Date range + daily summary table (target vs actual calories, difference, meal count, status)
 * - Detailed comparison for selected date (calories, macros, vitamins, minerals: target vs actual vs diff vs status)
 * - Dish plan vs logged meals by meal type (plan dishes, logged dishes, plan kcal, logged kcal, difference)
 * - Logged meals grouped by meal type with per-meal details (name, time, kcal, P/C/F/fiber, notes)
 *
 * Metrics we can compute with existing data (chart data + logged meals + dish plan + targets):
 * - Overall compliance score: e.g. avg(actual/target) per day or % of days "Met" in range
 * - Compliance vs last week: load previous week and compare
 * - Nutrient consistency (Macros/Micros %): variance or stability of daily actual vs target over range
 * - Meal logging frequency: expected meals (e.g. 4/day × days) vs logged count → "X / Y Meals", "Z% Reporting Rate"
 * - Daily adherence matrix: per day × meal type, ON TRACK / PARTIAL / MISSED from plan vs logged
 * - Nutrient balance radar: actual vs target for protein, carbs, fiber, fats, vitamins (from daily aggregates)
 * - Daily drill-down per meal: plan name, compliance %, kcal (we have plan vs logged by meal type)
 * - Adherence trend: current week % (from daily statuses)
 */

interface MealPlanTargets {
  totalCalories: number;
  macros: {
    protein_g: number;
    carbs_g: number;
    fat_g: number;
    fiber_g: number;
  };
  vitamins: Array<{ key: string; label: string; unit: string; value: number }>;
  minerals: Array<{ key: string; label: string; unit: string; value: number }>;
}

interface MealPlanMonitoringTabProps {
  patientId: number;
  goalId?: number;
  mealPlanTargets: MealPlanTargets;
}

interface DailyComparison {
  date: string;
  target: MealPlanTargets;
  actual: NutritionChartDataPoint | null;
  loggedMeals: NutritionDataResponse[];
  differences: {
    calories: number | null;
    macros: {
      protein_g: number | null;
      carbs_g: number | null;
      fat_g: number | null;
      fiber_g: number | null;
    };
    vitamins: Record<string, number | null>;
    minerals: Record<string, number | null>;
  };
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function getWeekDates(weekStart: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
}

/** Title row with info icon; click icon to show explanation popover, click outside to close */
function MonitoringCardTitleWithInfo({
  id,
  title,
  children,
}: {
  id: string;
  title: React.ReactNode;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      )
        return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="monitoring-card-title-row">
      <span className="monitoring-card-title">{title}</span>
      <button
        ref={triggerRef}
        type="button"
        className="monitoring-info-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-label="How is this calculated?"
        aria-expanded={open}
      >
        <Info size={16} />
      </button>
      {open && (
        <div
          ref={popoverRef}
          className="monitoring-info-popover"
          role="dialog"
          aria-labelledby={`${id}-title`}
        >
          <div id={`${id}-title`} className="monitoring-info-popover-title">
            How we calculate this
          </div>
          <div className="monitoring-info-popover-body">{children}</div>
        </div>
      )}
    </div>
  );
}

const MealPlanMonitoringTab: React.FC<MealPlanMonitoringTabProps> = ({
  patientId,
  goalId,
  mealPlanTargets,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeMealPlan, setActiveMealPlan] = useState<MealPlanSummary | null>(null);
  const [mealPlanVersion, setMealPlanVersion] = useState<MealPlanVersionResponse | null>(null);
  const [dailyComparisons, setDailyComparisons] = useState<DailyComparison[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<{ start: string; end: string }>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30); // Default to last one month
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    };
  });
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<{
    calories: boolean;
    macros: boolean;
    vitamins: boolean;
    minerals: boolean;
  }>({
    calories: true, // Calories always visible (not collapsible)
    macros: false,
    vitamins: false,
    minerals: false,
  });
  const [expandedMealTypes, setExpandedMealTypes] = useState<Record<string, boolean>>({});
  const [expandedMealPlanComparison, setExpandedMealPlanComparison] = useState<boolean>(false);
  const [expandedDetailedComparison, setExpandedDetailedComparison] = useState<boolean>(false);
  const [lastWeekCompliancePct, setLastWeekCompliancePct] = useState<number | null>(null);
  const [backendOverallCompliancePct, setBackendOverallCompliancePct] = useState<number | null>(null);
  const [backendComplianceByDay, setBackendComplianceByDay] = useState<Array<{ date: string; compliance_pct: number }> | null>(null);
  const [matrixWeekStart, setMatrixWeekStart] = useState<string | null>(null);
  const [mealLoggingStats, setMealLoggingStats] = useState<MealLoggingStatsResponse | null>(null);

  const loadMonitoringData = useCallback(async () => {
    if (!patientId) return;

    try {
      setLoading(true);
      setError(null);

      const data = await nutritionService.getMonitoringDashboard(
        patientId,
        selectedDateRange.start,
        selectedDateRange.end,
        {
          goalId,
          targetCalories: mealPlanTargets?.totalCalories && mealPlanTargets.totalCalories > 0
            ? mealPlanTargets.totalCalories
            : undefined,
        }
      );

      if (data.meal_plan_summary) {
        setActiveMealPlan({
          plan_id: data.meal_plan_summary.plan_id,
          user_id: data.meal_plan_summary.user_id,
          mode: data.meal_plan_summary.mode as MealPlanSummary['mode'],
          variant: data.meal_plan_summary.variant ?? undefined,
          status: data.meal_plan_summary.status as MealPlanSummary['status'],
          language: data.meal_plan_summary.language,
          cuisine_pattern: data.meal_plan_summary.cuisine_pattern,
          meals_per_day: data.meal_plan_summary.meals_per_day,
          snacks_per_day: data.meal_plan_summary.snacks_per_day,
          meal_timing_json: data.meal_plan_summary.meal_timing_json ?? undefined,
          approved_at: data.meal_plan_summary.approved_at ?? undefined,
          activated_at: data.meal_plan_summary.activated_at ?? undefined,
          name: data.meal_plan_summary.name ?? undefined,
          start_date: data.meal_plan_summary.start_date ?? undefined,
          end_date: data.meal_plan_summary.end_date ?? undefined,
          goal_id: data.meal_plan_summary.goal_id ?? undefined,
        });
      } else {
        setActiveMealPlan(null);
      }

      if (data.meal_plan_version) {
        const days: MealPlanDay[] = data.meal_plan_version.days.map((d: { date: string; items: unknown[] }) => ({
          date: typeof d.date === 'string' ? d.date : String(d.date).split('T')[0],
          items: Array.isArray(d.items) ? (d.items as MealPlanDay['items']) : [],
        }));
        setMealPlanVersion({
          plan_id: data.meal_plan_version.plan_id,
          version_id: data.meal_plan_version.version_id,
          status: 'approved',
          days,
        });
      } else {
        setMealPlanVersion(null);
      }

      const loggedMeals = data.logged_meals;
      const chartData = data.chart_data;

      const mealsByDate = new Map<string, NutritionDataResponse[]>();
      loggedMeals.forEach((meal) => {
        const dateStr = meal.meal_date.split('T')[0];
        if (!mealsByDate.has(dateStr)) mealsByDate.set(dateStr, []);
        mealsByDate.get(dateStr)!.push(meal);
      });

      const pointByDate = new Map<string, NutritionChartDataPoint>();
      chartData.data_points.forEach((point) => {
        const dateStr = typeof point.date === 'string' ? point.date.split('T')[0] : String(point.date).split('T')[0];
        pointByDate.set(dateStr, point);
      });

      const allDatesInRange: string[] = [];
      const start = new Date(selectedDateRange.start + 'T12:00:00');
      const end = new Date(selectedDateRange.end + 'T12:00:00');
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        allDatesInRange.push(d.toISOString().split('T')[0]);
      }

      const comparisons: DailyComparison[] = allDatesInRange.map((dateStr) => {
        const point = pointByDate.get(dateStr);
        const meals = mealsByDate.get(dateStr) || [];

        if (point) {
          const differences = {
            calories: mealPlanTargets.totalCalories > 0 ? point.calories - mealPlanTargets.totalCalories : null,
            macros: {
              protein_g: mealPlanTargets.macros.protein_g > 0 ? point.protein_g - mealPlanTargets.macros.protein_g : null,
              carbs_g: mealPlanTargets.macros.carbs_g > 0 ? point.carbs_g - mealPlanTargets.macros.carbs_g : null,
              fat_g: mealPlanTargets.macros.fat_g > 0 ? point.fat_g - mealPlanTargets.macros.fat_g : null,
              fiber_g: mealPlanTargets.macros.fiber_g > 0 ? point.fiber_g - mealPlanTargets.macros.fiber_g : null,
            },
            vitamins: {} as Record<string, number | null>,
            minerals: {} as Record<string, number | null>,
          };
          mealPlanTargets.vitamins.forEach((vit) => {
            differences.vitamins[vit.key] = vit.value > 0 ? ((point as unknown as Record<string, number>)[vit.key] ?? 0) - vit.value : null;
          });
          mealPlanTargets.minerals.forEach((min) => {
            differences.minerals[min.key] = min.value > 0 ? ((point as unknown as Record<string, number>)[min.key] ?? 0) - min.value : null;
          });
          return { date: dateStr, target: mealPlanTargets, actual: point, loggedMeals: meals, differences };
        }

        const emptyDifferences = {
          calories: mealPlanTargets.totalCalories > 0 ? -mealPlanTargets.totalCalories : null,
          macros: {
            protein_g: mealPlanTargets.macros.protein_g > 0 ? -mealPlanTargets.macros.protein_g : null,
            carbs_g: mealPlanTargets.macros.carbs_g > 0 ? -mealPlanTargets.macros.carbs_g : null,
            fat_g: mealPlanTargets.macros.fat_g > 0 ? -mealPlanTargets.macros.fat_g : null,
            fiber_g: mealPlanTargets.macros.fiber_g > 0 ? -mealPlanTargets.macros.fiber_g : null,
          },
          vitamins: {} as Record<string, number | null>,
          minerals: {} as Record<string, number | null>,
        };
        mealPlanTargets.vitamins.forEach((vit) => { emptyDifferences.vitamins[vit.key] = vit.value > 0 ? -vit.value : null; });
        mealPlanTargets.minerals.forEach((min) => { emptyDifferences.minerals[min.key] = min.value > 0 ? -min.value : null; });
        return { date: dateStr, target: mealPlanTargets, actual: null, loggedMeals: meals, differences: emptyDifferences };
      });

      setDailyComparisons(comparisons.sort((a, b) => a.date.localeCompare(b.date)));
      setMealLoggingStats(data.meal_logging_stats);
      setLastWeekCompliancePct(data.last_week_compliance_pct ?? null);
      setBackendOverallCompliancePct(data.overall_compliance_pct ?? null);
      setBackendComplianceByDay(data.compliance_by_day ?? null);
    } catch (err: any) {
      console.error('Failed to load monitoring data:', err);
      
      // Handle different error formats (similar to CreatePatientPage pattern)
      const getErrorMessage = (e: any): string => {
        if (typeof e === 'string') return e;
        if (e?.message) return e.message;
        if (e?.response?.data) {
          const data = e.response.data;
          if (typeof data === 'string') return data;
          
          // Handle FastAPI error format: { detail: string | array | object }
          if (data?.detail) {
            const detail = data.detail;
            if (typeof detail === 'string') return detail;
            
            // FastAPI 422 validation errors come as array of {loc, msg, type}
            if (Array.isArray(detail)) {
              const prettyFromArray = (items: any[]): string => {
                const humanize = (locParts: any[]): string => {
                  if (!Array.isArray(locParts)) return '';
                  const filtered = locParts.filter(
                    (p) => typeof p === 'string' && p !== 'body'
                  );
                  const joined = filtered.join(' > ').replace(/_/g, ' ');
                  return joined.charAt(0).toUpperCase() + joined.slice(1);
                };
                const parts = items.map((d) => {
                  const loc = humanize(d?.loc || []);
                  const msg = d?.msg || d?.message || JSON.stringify(d);
                  return loc ? `${loc}: ${msg}` : msg;
                });
                return parts.join('; ');
              };
              return prettyFromArray(detail);
            }
            
            // If detail is an object, try to extract message
            if (typeof detail === 'object') {
              if (detail?.message) return detail.message;
              if (detail?.error_code && detail?.message) return detail.message;
              return JSON.stringify(detail);
            }
          }
          
          // Try direct message field
          if (data?.message) return data.message;
        }
        return 'Failed to load monitoring data';
      };
      
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [patientId, goalId, mealPlanTargets, selectedDateRange]);

  useEffect(() => {
    loadMonitoringData();
  }, [loadMonitoringData]);

  useEffect(() => {
    const start = selectedDateRange.start;
    if (!matrixWeekStart || matrixWeekStart < start || addDays(matrixWeekStart, 6) > selectedDateRange.end) {
      setMatrixWeekStart(start);
    }
  }, [selectedDateRange.start, selectedDateRange.end, matrixWeekStart]);

  const getStatusColor = (diff: number | null, isOver: boolean = false): string => {
    if (diff === null) return '#9ca3af';
    if (Math.abs(diff) < 0.01) return '#10b981'; // Met (green)
    if (isOver && diff > 0) return '#f59e0b'; // Over (yellow)
    if (!isOver && diff < 0) return '#ef4444'; // Under (red)
    return '#6b7280';
  };

  const getStatusLabel = (diff: number | null): string => {
    if (diff === null) return '-';
    if (Math.abs(diff) < 0.01) return '✓ Met';
    if (diff > 0) return '↑ Over';
    return '↓ Under';
  };

  const formatNutrientKey = (key: string): string => {
    return key
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const selectedComparison = selectedDate
    ? dailyComparisons.find(c => c.date === selectedDate)
    : dailyComparisons.length > 0
    ? dailyComparisons[dailyComparisons.length - 1]
    : null;

  const selectedDateForDrill = selectedComparison?.date ?? null;

  const {
    overallCompliancePct,
    complianceVsLastWeekPct,
    macrosConsistencyPct,
    microsConsistencyPct,
    totalLoggedMeals,
    expectedMeals,
    reportingRatePct,
    adherenceMatrix,
    adherenceMatrixVisibleWeek,
    radarChartData,
    drillDownMeals,
    currentWeekAdherencePct,
    complianceBarSegments,
  } = useMemo(() => {
    const days = dailyComparisons.length;
    const uniqueFromPlan = mealPlanVersion?.days
      ? Array.from(new Set(mealPlanVersion.days.flatMap((d) => (d.items || []).map((i) => (i.meal_key || '').toLowerCase().replace(/\s+/g, '_'))).filter(Boolean)))
      : [];
    const mealTypes: string[] =
      uniqueFromPlan.length > 0
        ? ([] as string[]).concat(
            [...DEFAULT_MEAL_TYPE_ORDER].filter((k) => uniqueFromPlan.includes(k)),
            uniqueFromPlan.filter((k) => !DEFAULT_MEAL_TYPE_ORDER.includes(k as (typeof DEFAULT_MEAL_TYPE_ORDER)[number])),
          )
        : [...DEFAULT_MEAL_TYPE_ORDER];

    let macroSum = 0;
    let microSum = 0;
    let macroCount = 0;
    let microCount = 0;
    dailyComparisons.forEach(comp => {
      const a = comp.actual;
      const t = comp.target;
      if (a && t.macros) {
        const p = t.macros.protein_g > 0 ? Math.min(100, (a.protein_g / t.macros.protein_g) * 100) : 100;
        const c = t.macros.carbs_g > 0 ? Math.min(100, (a.carbs_g / t.macros.carbs_g) * 100) : 100;
        const f = t.macros.fat_g > 0 ? Math.min(100, (a.fat_g / t.macros.fat_g) * 100) : 100;
        macroSum += (p + c + f) / 3;
        macroCount++;
      }
      if (a && t.vitamins?.length) {
        let vSum = 0;
        t.vitamins.forEach(v => {
          const av = (a as any)[v.key] ?? 0;
          vSum += v.value > 0 ? Math.min(100, (av / v.value) * 100) : 100;
        });
        microSum += t.vitamins.length > 0 ? vSum / t.vitamins.length : 0;
        microCount++;
      }
    });
    const macrosConsistencyPct = macroCount > 0 ? Math.round(macroSum / macroCount) : 0;
    const microsConsistencyPct = microCount > 0 ? Math.round(microSum / microCount) : 0;

    const totalLoggedMeals = dailyComparisons.reduce((sum, c) => sum + c.loggedMeals.length, 0);
    const expectedMeals = days * 4;
    const reportingRatePct = expectedMeals > 0 ? Math.round((totalLoggedMeals / expectedMeals) * 100) : 0;

    const adherenceMatrix: { date: string; dayLabel: string; cells: Record<string, 'on_track' | 'partial' | 'missed' | 'none'> }[] = [];
    dailyComparisons.forEach(comp => {
      const cells: Record<string, 'on_track' | 'partial' | 'missed' | 'none'> = {};
      const mealPlanDay = mealPlanVersion?.days.find(d => d.date.split('T')[0] === comp.date);
      const loggedByType = (comp.loggedMeals as NutritionDataResponse[]).reduce((acc, m) => {
        const k = m.meal_type.toLowerCase();
        if (!acc[k]) acc[k] = [];
        acc[k].push(m);
        return acc;
      }, {} as Record<string, NutritionDataResponse[]>);
      mealTypes.forEach((mt: string) => {
        const norm = (k: string) => (k || '').toLowerCase().replace(/\s+/g, '_');
        const planItems = mealPlanDay?.items.filter((i) => norm(i.meal_key || '') === mt) ?? [];
        const logged = loggedByType[mt] ?? [];
        if (planItems.length === 0 && logged.length === 0) {
          cells[mt] = 'none';
        } else if (planItems.length === 0) {
          cells[mt] = 'on_track';
        } else if (logged.length === 0) {
          cells[mt] = 'missed';
        } else {
          const planCal = planItems.reduce((s, i) => s + (i.dish_nutrients?.values?.calories ?? 0), 0);
          const loggedCal = logged.reduce((s, m) => s + (m.calories ?? 0), 0);
          const ratio = planCal > 0 ? loggedCal / planCal : 1;
          if (ratio >= 0.85 && ratio <= 1.15) cells[mt] = 'on_track';
          else if (ratio >= 0.5) cells[mt] = 'partial';
          else cells[mt] = 'missed';
        }
      });
      const d = new Date(comp.date);
      adherenceMatrix.push({
        date: comp.date,
        dayLabel: d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase().slice(0, 3) + ' ' + d.getDate(),
        cells,
      });
    });

    // Overall compliance: from backend (weighted nutrient + macro + micro + adherence)
    const overallCompliancePct = backendOverallCompliancePct !== null ? backendOverallCompliancePct : 0;
    const lastWeek = lastWeekCompliancePct ?? 0;
    const complianceVsLastWeekPct = lastWeek > 0 ? (overallCompliancePct - lastWeek).toFixed(1) : null;

    const weekStart = matrixWeekStart ?? selectedDateRange.start;
    const weekDates = getWeekDates(weekStart);
    const matrixByDate = new Map(adherenceMatrix.map(r => [r.date, r]));
    const emptyCells: Record<string, 'on_track' | 'partial' | 'missed' | 'none'> = Object.fromEntries(mealTypes.map((mt) => [mt, 'none']));
    const adherenceMatrixVisibleWeek = weekDates.map(date => {
      const existing = matrixByDate.get(date);
      if (existing) return existing;
      const d = new Date(date + 'T12:00:00');
      return {
        date,
        dayLabel: d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase().slice(0, 3) + ' ' + d.getDate(),
        cells: { ...emptyCells },
      };
    });

    const sel = selectedComparison?.actual;
    const nutrientBalanceData = [
      { label: 'Protein', target: mealPlanTargets.macros.protein_g, actual: sel?.protein_g ?? 0 },
      { label: 'Carbs', target: mealPlanTargets.macros.carbs_g, actual: sel?.carbs_g ?? 0 },
      { label: 'Fiber', target: mealPlanTargets.macros.fiber_g, actual: sel?.fiber_g ?? 0 },
      { label: 'Fats', target: mealPlanTargets.macros.fat_g, actual: sel?.fat_g ?? 0 },
    ];
    const vitaminsTarget = mealPlanTargets.vitamins?.length
      ? mealPlanTargets.vitamins.reduce((s, v) => s + (v.value > 0 ? 1 : 0), 0)
      : 0;
    const vitaminsActualPct =
      vitaminsTarget > 0 && sel
        ? mealPlanTargets.vitamins.reduce((sum, v) => {
            if (v.value <= 0) return sum;
            const av = (sel as unknown as Record<string, number>)[v.key] ?? 0;
            return sum + Math.min(100, (av / v.value) * 100);
          }, 0) / vitaminsTarget
        : 0;
    const radarChartData = [
      { subject: 'PROTEIN', target: 100, actual: Math.min(100, mealPlanTargets.macros.protein_g > 0 ? ((sel?.protein_g ?? 0) / mealPlanTargets.macros.protein_g) * 100 : 0) },
      { subject: 'CARBS', target: 100, actual: Math.min(100, mealPlanTargets.macros.carbs_g > 0 ? ((sel?.carbs_g ?? 0) / mealPlanTargets.macros.carbs_g) * 100 : 0) },
      { subject: 'FIBER', target: 100, actual: Math.min(100, mealPlanTargets.macros.fiber_g > 0 ? ((sel?.fiber_g ?? 0) / mealPlanTargets.macros.fiber_g) * 100 : 0) },
      { subject: 'VITAMINS', target: 100, actual: Math.round(vitaminsActualPct) },
      { subject: 'FATS', target: 100, actual: Math.min(100, mealPlanTargets.macros.fat_g > 0 ? ((sel?.fat_g ?? 0) / mealPlanTargets.macros.fat_g) * 100 : 0) },
    ];

    // Drill-down compliance must match matrix logic:
    // - No plan + no log → 'none' (not in plan). No plan + logged → 'match' (on track).
    // - Plan + no log → 'missed'. Plan + logged → compare ratio → match / partial / missed.
    const drillDownMeals: { mealType: string; planName: string; compliance: 'match' | 'partial' | 'missed' | 'none'; kcal: number; deviationNote?: string }[] = [];
    if (selectedDateForDrill && mealPlanVersion && selectedComparison) {
      const mealPlanDay = mealPlanVersion.days.find(d => d.date.split('T')[0] === selectedDateForDrill);
      const loggedByType = (selectedComparison.loggedMeals as NutritionDataResponse[]).reduce((acc, m) => {
        const k = m.meal_type.toLowerCase();
        if (!acc[k]) acc[k] = [];
        acc[k].push(m);
        return acc;
      }, {} as Record<string, NutritionDataResponse[]>);
      mealTypes.forEach((mt: string) => {
        const norm = (k: string) => (k || '').toLowerCase().replace(/\s+/g, '_');
        const planItems = mealPlanDay?.items.filter((i) => norm(i.meal_key || '') === mt) ?? [];
        const logged = loggedByType[mt] ?? [];
        const planName = planItems.map(i => i.dish_name).join(', ') || '—';
        const kcal = logged.reduce((s, m) => s + (m.calories ?? 0), 0);
        let compliance: 'match' | 'partial' | 'missed' | 'none' = 'missed';
        let deviationNote: string | undefined;
        if (planItems.length === 0 && logged.length === 0) {
          compliance = 'none'; // Not in plan, nothing to log — same as matrix "none"
        } else if (planItems.length === 0 && logged.length > 0) {
          compliance = 'match'; // No plan for this meal type; user logged → on track (align with matrix)
        } else if (planItems.length > 0 && logged.length > 0) {
          const planCal = planItems.reduce((s, i) => s + (i.dish_nutrients?.values?.calories ?? 0), 0);
          const ratio = planCal > 0 ? kcal / planCal : 1;
          if (ratio >= 0.85 && ratio <= 1.15) compliance = 'match';
          else if (ratio >= 0.5) {
            compliance = 'partial';
            deviationNote = logged[0]?.notes || 'Logged intake differs from plan.';
          }
        }
        // else: plan exists, nothing logged → stays 'missed'
        drillDownMeals.push({ mealType: mt, planName, compliance, kcal, deviationNote });
      });
    }

    const complianceBarSegments = (backendComplianceByDay && backendComplianceByDay.length > 0)
      ? backendComplianceByDay.map(({ compliance_pct: pct }) => ({
          widthPct: 100 / backendComplianceByDay.length,
          heightPct: Math.min(100, Math.max(0, pct)),
          color: pct >= 90 ? '#6366f1' : pct >= 70 ? '#a5b4fc' : '#c7d2fe',
        }))
      : [{ widthPct: 100, heightPct: 0, color: '#e5e7eb' }];

    return {
      overallCompliancePct,
      complianceVsLastWeekPct,
      macrosConsistencyPct,
      microsConsistencyPct,
      totalLoggedMeals,
      expectedMeals,
      reportingRatePct,
      adherenceMatrix,
      adherenceMatrixVisibleWeek,
      nutrientBalanceData,
      radarChartData,
      drillDownMeals,
      currentWeekAdherencePct: overallCompliancePct,
      complianceBarSegments,
    };
  }, [
    dailyComparisons,
    mealPlanTargets,
    lastWeekCompliancePct,
    backendOverallCompliancePct,
    backendComplianceByDay,
    mealPlanVersion,
    selectedComparison,
    selectedDateForDrill,
    matrixWeekStart,
    selectedDateRange,
  ]);

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading monitoring data...</div>;
  }

  if (error) {
    const errorMessage = typeof error === 'string' ? error : JSON.stringify(error);
    return (
      <div style={{ padding: '20px', color: '#ef4444' }}>
        Error: {errorMessage}
      </div>
    );
  }

  return (
    <div className="monitoring-wrap">
      {/* Top bar: Refresh left, date range picker at rightmost */}
      <div className="monitoring-top-bar">
        <button type="button" className="monitoring-refresh-btn" onClick={() => loadMonitoringData()}>
          Refresh
        </button>
        <div className="monitoring-top-bar-spacer" />
        <CalendarDateRangePicker
          startDate={selectedDateRange.start}
          endDate={selectedDateRange.end}
          onChange={(range) => setSelectedDateRange({ start: range.start, end: range.end })}
          placeholder="Select date range"
          className="monitoring-date-range-picker"
        />
      </div>

      {/* Card row 1: Overall Compliance, Nutrient Consistency, Meal Logging */}
      {dailyComparisons.length > 0 && (
        <div className="monitoring-cards-grid">
          <div className="monitoring-card">
            <MonitoringCardTitleWithInfo id="overall-compliance" title="Overall Compliance Score">
              <p>Overall compliance is a <strong>weighted average</strong> of four components (25% each):</p>
              <p><strong>1. Nutrient (calories)</strong>: How close daily intake is to your calorie target. 90–110% of target = 100%; otherwise the ratio is used (capped 0–150%).</p>
              <p><strong>2. Macros</strong>: How close protein, carbs, and fat are to targets for that day (average of the three, capped at 100% each).</p>
              <p><strong>3. Micros</strong>: How close vitamins and minerals are to targets for that day (average across vitamins and minerals, capped at 100% each).</p>
              <p><strong>4. Adherence</strong>: From the Daily Adherence Matrix — on track = 100%, partial = 50%, missed = 0%. Slots not in the plan are excluded.</p>
              <p><strong>Overall %</strong> = average of these daily composite scores over the selected range. The bar below shows each day’s composite score.</p>
              <p><strong>Vs last week</strong> = comparison to the previous period (calorie-based in the backend).</p>
            </MonitoringCardTitleWithInfo>
            <div className="monitoring-compliance-score">{overallCompliancePct}%</div>
            {complianceVsLastWeekPct != null && (
              <div className="monitoring-compliance-vs">
                {Number(complianceVsLastWeekPct) >= 0 ? '+' : ''}{complianceVsLastWeekPct}% vs last week
              </div>
            )}
            <div className="monitoring-compliance-bar-wrap">
              {complianceBarSegments.map((seg, i) => (
                <div
                  key={i}
                  className="monitoring-compliance-bar-segment"
                  style={{
                    width: `${seg.widthPct}%`,
                    height: `${seg.heightPct}%`,
                    backgroundColor: seg.color,
                  }}
                />
              ))}
            </div>
          </div>
          <div className="monitoring-card">
            <MonitoringCardTitleWithInfo id="nutrient-consistency" title="Nutrient Consistency">
              <p><strong>Macros</strong>: For each day we compare actual vs target for protein, carbs, and fat. Each nutrient gets a score up to 100% (actual ÷ target, capped at 100%). We average the three and then average across all days.</p>
              <p><strong>Micros</strong>: Same idea for vitamins — we average (actual ÷ target, capped at 100%) across vitamins and then across days.</p>
              <p>Higher % means your intake is more consistently aligned with your targets over the period.</p>
            </MonitoringCardTitleWithInfo>
            <div className="monitoring-consistency-row">
              <span className="monitoring-consistency-label">Macros</span>
              <span className="monitoring-consistency-value">{macrosConsistencyPct}%</span>
            </div>
            <div className="monitoring-consistency-bar">
              <div
                className="monitoring-consistency-bar-fill"
                style={{ width: `${macrosConsistencyPct}%`, backgroundColor: '#6366f1' }}
              />
            </div>
            <div className="monitoring-consistency-row">
              <span className="monitoring-consistency-label">Micros</span>
              <span className="monitoring-consistency-value">{microsConsistencyPct}%</span>
            </div>
            <div className="monitoring-consistency-bar">
              <div
                className="monitoring-consistency-bar-fill"
                style={{ width: `${microsConsistencyPct}%`, backgroundColor: '#a78bfa' }}
              />
            </div>
            <span className="monitoring-consistency-badge">STABLE</span>
          </div>
          <div className="monitoring-card">
            <MonitoringCardTitleWithInfo id="meal-logging" title="Meal Logging Frequency">
              <p><strong>Count (left)</strong>: For each day we count how many meal types (breakfast, lunch, dinner, snack) have at least one log. We sum that count across all days in the range. So multiple items at breakfast still count as one “breakfast” slot.</p>
              <p><strong>Expected (right)</strong>: Days in range × (meals per day + snacks per day) from your active meal plan. E.g. 3 meals + 1 snack = 4 slots per day.</p>
              <p><strong>Reporting rate</strong> = (count ÷ expected) × 100%. Over 100% means you logged more meal types than the plan expects (e.g. extra snacks).</p>
            </MonitoringCardTitleWithInfo>
            <div className="monitoring-logging-wrap">
              <div className="monitoring-logging-circle">
                {mealLoggingStats?.total_logged_meal_slots ?? totalLoggedMeals}
              </div>
              <div>
                <div className="monitoring-logging-text">
                  {mealLoggingStats?.total_logged_meal_slots ?? totalLoggedMeals} / {mealLoggingStats?.expected_meals ?? expectedMeals} Meals
                </div>
                <div className="monitoring-logging-sub">
                  {mealLoggingStats?.reporting_rate_pct ?? reportingRatePct}% Reporting Rate
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bottom row: Daily Adherence Matrix (left ~2/3) + Nutrient Balance radar (right ~1/3) */}
      {(adherenceMatrix.length > 0 || dailyComparisons.length > 0) && (
        <div className="monitoring-matrix-and-radar">
          {/* Daily Adherence Matrix – left, daily table structure */}
          {adherenceMatrix.length > 0 && (
            <div className="monitoring-card monitoring-matrix-card">
              <div className="monitoring-matrix-header">
                <div className="monitoring-matrix-header-left">
                  <MonitoringCardTitleWithInfo id="adherence-matrix" title="Daily Adherence Matrix">
                    <p>Each cell is one meal type (breakfast, lunch, dinner, snack) on one day.</p>
                    <p><strong>Green (On track)</strong>: No plan for that slot and you didn’t log, or you logged and calories are 85–115% of the plan for that meal.</p>
                    <p><strong>Orange (Partial)</strong>: You logged but calories are 50–85% (or &gt;115%) of the plan.</p>
                    <p><strong>Red (Missed)</strong>: The plan had a meal for that slot but nothing was logged.</p>
                    <p><strong>Grey</strong>: No plan for that meal type that day and nothing logged (not in plan).</p>
                    <p>Click a day to see details in the Daily Drill-down below.</p>
                  </MonitoringCardTitleWithInfo>
                  <div className="monitoring-matrix-subtitle">Weekly compliance by meal type</div>
                </div>
                <div className="monitoring-matrix-legend">
                  <span className="monitoring-matrix-legend-item">
                    <span className="monitoring-matrix-legend-dot monitoring-matrix-legend-dot-on-track" />
                    ON TRACK
                  </span>
                  <span className="monitoring-matrix-legend-item">
                    <span className="monitoring-matrix-legend-dot monitoring-matrix-legend-dot-partial" />
                    PARTIAL
                  </span>
                  <span className="monitoring-matrix-legend-item">
                    <span className="monitoring-matrix-legend-dot monitoring-matrix-legend-dot-missed" />
                    MISSED
                  </span>
                </div>
              </div>
              {(() => {
                const effectiveWeekStart = matrixWeekStart ?? selectedDateRange.start;
                const weekEnd = addDays(effectiveWeekStart, 6);
                const canGoPrev = effectiveWeekStart > selectedDateRange.start;
                const canGoNext = weekEnd < selectedDateRange.end;
                const lastAllowedWeekStart = addDays(selectedDateRange.end, -6);
                return (
                  <div className="monitoring-matrix-week-scroller">
                    <button
                      type="button"
                      className="monitoring-matrix-week-btn"
                      disabled={!canGoPrev}
                      onClick={() => {
                        const newStart = addDays(effectiveWeekStart, -7);
                        setMatrixWeekStart(newStart < selectedDateRange.start ? selectedDateRange.start : newStart);
                      }}
                      aria-label="Previous week"
                    >
                      <ChevronLeft size={18} />
                      <span>Previous week</span>
                    </button>
                    <span className="monitoring-matrix-week-label">
                      {new Date(effectiveWeekStart + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {' – '}
                      {new Date(weekEnd + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <button
                      type="button"
                      className="monitoring-matrix-week-btn"
                      disabled={!canGoNext}
                      onClick={() => {
                        const newStart = addDays(effectiveWeekStart, 7);
                        setMatrixWeekStart(newStart > lastAllowedWeekStart ? lastAllowedWeekStart : newStart);
                      }}
                      aria-label="Next week"
                    >
                      <span>Next week</span>
                      <ChevronRight size={18} />
                    </button>
                  </div>
                );
              })()}
              <table className="monitoring-matrix-table">
                <thead>
                  <tr>
                    <th></th>
                    {adherenceMatrixVisibleWeek.map(row => {
                      const today = new Date().toISOString().split('T')[0];
                      const isToday = row.date === today;
                      const isSelected = row.date === selectedDateForDrill;
                      const hasData = dailyComparisons.some(c => c.date === row.date);
                      const thClass = [
                        isToday ? 'monitoring-matrix-th-today' : '',
                        isSelected ? 'monitoring-matrix-th-selected' : '',
                        hasData ? 'monitoring-matrix-th-clickable' : '',
                      ].filter(Boolean).join(' ');
                      return (
                        <th
                          key={row.date}
                          className={thClass}
                          onClick={() => hasData && setSelectedDate(row.date)}
                          onKeyDown={(e) => hasData && (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setSelectedDate(row.date))}
                          role={hasData ? 'button' : undefined}
                          tabIndex={hasData ? 0 : undefined}
                          title={hasData ? `View ${row.dayLabel} in Daily Drill-down` : undefined}
                        >
                          {row.dayLabel}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {['breakfast', 'lunch', 'dinner', 'snack'].map(mt => (
                    <tr key={mt}>
                      <td>{mt}</td>
                      {adherenceMatrixVisibleWeek.map(row => {
                        const status = row.cells[mt];
                        const statusClass = status === 'on_track' ? 'on-track' : status;
                        const hasData = dailyComparisons.some(c => c.date === row.date);
                        const isSelected = row.date === selectedDateForDrill;
                        return (
                          <td
                            key={row.date}
                            className={isSelected ? 'monitoring-matrix-td-selected' : hasData ? 'monitoring-matrix-td-clickable' : ''}
                            onClick={() => hasData && setSelectedDate(row.date)}
                            onKeyDown={(e) => hasData && (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setSelectedDate(row.date))}
                            role={hasData ? 'button' : undefined}
                            tabIndex={hasData ? 0 : undefined}
                            title={hasData ? `View ${row.dayLabel} in Daily Drill-down` : undefined}
                          >
                            <div
                              className={`monitoring-matrix-cell monitoring-matrix-cell-${statusClass}`}
                              title={status}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Nutrient Balance – right: hexagonal radar (Actual vs Target) */}
          {dailyComparisons.length > 0 && (
            <div className="monitoring-card monitoring-card-nutrient-balance">
              <div className="monitoring-card-title">Nutrient Balance</div>
              <div className="monitoring-card-subtitle">Actual vs. Target Profile</div>
              <div className="monitoring-radar-wrap">
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChartBox cx="50%" cy="50%" outerRadius="70%" data={radarChartData}>
                    <PolarGridBox stroke="#e5e7eb" />
                    <PolarAngleAxisBox
                      dataKey="subject"
                      tick={{ fontSize: 11, fill: '#374151', fontWeight: 600 }}
                      tickLine={false}
                    />
                    <PolarRadiusAxisBox angle={90} domain={[0, 100]} tick={false} axisLine={false} />
                    <RadarBox
                      name="Target Baseline"
                      dataKey="target"
                      stroke="#9ca3af"
                      fill="#9ca3af"
                      fillOpacity={0.15}
                      strokeWidth={1}
                    />
                    <RadarBox
                      name="Actual Intake"
                      dataKey="actual"
                      stroke="#6366f1"
                      fill="#6366f1"
                      fillOpacity={0.5}
                      strokeWidth={2}
                    />
                  </RadarChartBox>
                </ResponsiveContainer>
              </div>
              <div className="monitoring-radar-legend">
                <span className="monitoring-radar-legend-item">
                  <span className="monitoring-radar-legend-dot monitoring-radar-legend-dot-actual" />
                  ACTUAL INTAKE
                </span>
                <span className="monitoring-radar-legend-item">
                  <span className="monitoring-radar-legend-dot monitoring-radar-legend-dot-target" />
                  TARGET BASELINE
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Two-col: Daily Drill-down | Adherence Trend */}
      {dailyComparisons.length > 0 && (
      <>

      <div className="monitoring-two-col">
        <div>
          <div className="monitoring-card">
            <div className="monitoring-drilldown-header">
              <h3 className="monitoring-drilldown-title">Daily Drill-down</h3>
              <div className="monitoring-drilldown-date-nav">
                <button
                  type="button"
                  onClick={() => {
                    const idx = dailyComparisons.findIndex(c => c.date === selectedDateForDrill);
                    if (idx > 0) setSelectedDate(dailyComparisons[idx - 1].date);
                  }}
                  disabled={!selectedDateForDrill || dailyComparisons.findIndex(c => c.date === selectedDateForDrill) <= 0}
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="monitoring-drilldown-date-text">
                  {selectedDateForDrill
                    ? new Date(selectedDateForDrill).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })
                    : '—'}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const idx = dailyComparisons.findIndex(c => c.date === selectedDateForDrill);
                    if (idx >= 0 && idx < dailyComparisons.length - 1) setSelectedDate(dailyComparisons[idx + 1].date);
                  }}
                  disabled={!selectedDateForDrill || dailyComparisons.findIndex(c => c.date === selectedDateForDrill) >= dailyComparisons.length - 1}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
            {drillDownMeals.map((meal, i) => {
              const knownMealIconTypes = ['breakfast', 'lunch', 'dinner', 'snack'];
              const iconSuffix = knownMealIconTypes.includes(meal.mealType) ? meal.mealType : 'other';
              const iconClass = `monitoring-meal-card-icon monitoring-meal-card-icon-${iconSuffix}`;
              const Icon = meal.mealType === 'breakfast' ? Sun : meal.mealType === 'lunch' ? UtensilsCrossed : meal.mealType === 'dinner' ? Moon : Cookie;
              const complianceClass =
                meal.compliance === 'match'
                  ? 'monitoring-meal-card-compliance-match'
                  : meal.compliance === 'partial'
                  ? 'monitoring-meal-card-compliance-partial'
                  : meal.compliance === 'none'
                  ? 'monitoring-meal-card-compliance-none'
                  : 'monitoring-meal-card-compliance-missed';
              const complianceText =
                meal.compliance === 'match'
                  ? 'COMPLIANCE: 100% MATCH'
                  : meal.compliance === 'partial'
                  ? 'COMPLIANCE: PARTIAL DEVIATION'
                  : meal.compliance === 'none'
                  ? 'NOT IN PLAN'
                  : 'COMPLIANCE: MISSED';
              return (
                <div key={i} className="monitoring-meal-card">
                  <div className={iconClass}>
                    <Icon size={22} />
                  </div>
                  <div className="monitoring-meal-card-body">
                    <div className="monitoring-meal-card-name">{meal.mealType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</div>
                    <div className="monitoring-meal-card-plan">Plan: {meal.planName}</div>
                    <div className={`monitoring-meal-card-compliance ${complianceClass}`}>{complianceText}</div>
                    <div className="monitoring-meal-card-kcal">{Math.round(meal.kcal)} kcal</div>
                    {meal.deviationNote && (
                      <div className="monitoring-meal-deviation-note">
                        <AlertTriangle size={18} />
                        <span>{meal.deviationNote}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="monitoring-card">
          <div className="monitoring-card-title">Adherence Trend</div>
          <div className="monitoring-trend-value">{currentWeekAdherencePct}%</div>
          <div className="monitoring-trend-label">Current Week</div>
          <div className="monitoring-trend-weeks">
            <span>WK 01</span>
            <span>WK 02</span>
            <span>WK 03</span>
            <span className="active">PRESENT</span>
          </div>
        </div>
      </div>
      </>
      )}

      {/* Collapsible: Detailed Comparison (legacy table) */}
      {selectedComparison && (
        <div className="monitoring-card" style={{ marginTop: 24 }}>
          <button
            type="button"
            onClick={() => setExpandedDetailedComparison(!expandedDetailedComparison)}
            style={{
              width: '100%',
              padding: '12px 0',
              textAlign: 'left',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span>{expandedDetailedComparison ? '▼' : '▶'}</span>
            Detailed Comparison: {new Date(selectedComparison.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </button>
          {expandedDetailedComparison && (
            <>
          {/* Nutrient Comparison Table */}
          <div style={{ marginBottom: '24px', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', position: 'sticky', left: 0, backgroundColor: '#f9fafb', zIndex: 10 }}>Nutrient</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, fontSize: '0.875rem' }}>Target</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, fontSize: '0.875rem' }}>Actual</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, fontSize: '0.875rem' }}>Difference</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, fontSize: '0.875rem' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {/* Calories - Always visible */}
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 500, position: 'sticky', left: 0, backgroundColor: 'white' }}>Calories</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>{selectedComparison.target.totalCalories.toFixed(0)} kcal</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>
                    {(selectedComparison.actual?.calories ?? 0).toFixed(0)} kcal
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: getStatusColor(selectedComparison.differences.calories), fontWeight: 600 }}>
                    {selectedComparison.differences.calories !== null
                      ? `${selectedComparison.differences.calories > 0 ? '+' : ''}${selectedComparison.differences.calories.toFixed(0)} kcal`
                      : '-'}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <span style={{ color: getStatusColor(selectedComparison.differences.calories), fontWeight: 600, fontSize: '0.85rem' }}>
                      {getStatusLabel(selectedComparison.differences.calories)}
                    </span>
                  </td>
                </tr>

                {/* Macros - Collapsible */}
                <tr 
                  style={{ 
                    backgroundColor: '#f3f4f6', 
                    borderBottom: '1px solid #e5e7eb',
                    cursor: 'pointer'
                  }}
                  onClick={() => setExpandedSections({ ...expandedSections, macros: !expandedSections.macros })}
                >
                  <td colSpan={5} style={{ padding: '10px 12px', fontWeight: 600, fontSize: '0.9rem', position: 'sticky', left: 0, backgroundColor: '#f3f4f6' }}>
                    <span style={{ marginRight: '8px' }}>{expandedSections.macros ? '▼' : '▶'}</span>
                    Macronutrients
                  </td>
                </tr>
                {expandedSections.macros && Object.entries(selectedComparison.target.macros).map(([key, targetValue]) => {
                  const actualValue = (selectedComparison.actual as any)?.[key] || 0;
                  const diff = selectedComparison.differences.macros[key as keyof typeof selectedComparison.differences.macros];
                  const unit = key === 'fiber_g' ? 'g' : 'g';
                  return (
                    <tr key={key} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '10px 12px 10px 32px', fontWeight: 500, position: 'sticky', left: 0, backgroundColor: 'white' }}>
                        {formatNutrientKey(key)}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>{targetValue.toFixed(1)} {unit}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>
                        {actualValue.toFixed(1)} {unit}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: getStatusColor(diff), fontWeight: 600 }}>
                        {diff !== null ? `${diff > 0 ? '+' : ''}${diff.toFixed(1)} ${unit}` : '-'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <span style={{ color: getStatusColor(diff), fontWeight: 600, fontSize: '0.85rem' }}>
                          {getStatusLabel(diff)}
                        </span>
                      </td>
                    </tr>
                  );
                })}

                {/* Vitamins - Collapsible */}
                <tr 
                  style={{ 
                    backgroundColor: '#f3f4f6', 
                    borderBottom: '1px solid #e5e7eb',
                    cursor: 'pointer'
                  }}
                  onClick={() => setExpandedSections({ ...expandedSections, vitamins: !expandedSections.vitamins })}
                >
                  <td colSpan={5} style={{ padding: '10px 12px', fontWeight: 600, fontSize: '0.9rem', position: 'sticky', left: 0, backgroundColor: '#f3f4f6' }}>
                    <span style={{ marginRight: '8px' }}>{expandedSections.vitamins ? '▼' : '▶'}</span>
                    Vitamins
                  </td>
                </tr>
                {expandedSections.vitamins && selectedComparison.target.vitamins.map(vit => {
                  const actualValue = (selectedComparison.actual as any)?.[vit.key] || 0;
                  const diff = selectedComparison.differences.vitamins[vit.key];
                  return (
                    <tr key={vit.key} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '10px 12px 10px 32px', fontWeight: 500, position: 'sticky', left: 0, backgroundColor: 'white' }}>
                        {vit.label}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>{vit.value.toFixed(1)} {vit.unit}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>
                        {actualValue.toFixed(1)} {vit.unit}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: getStatusColor(diff), fontWeight: 600 }}>
                        {diff !== null ? `${diff > 0 ? '+' : ''}${diff.toFixed(1)} ${vit.unit}` : '-'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <span style={{ color: getStatusColor(diff), fontWeight: 600, fontSize: '0.85rem' }}>
                          {getStatusLabel(diff)}
                        </span>
                      </td>
                    </tr>
                  );
                })}

                {/* Minerals - Collapsible */}
                <tr 
                  style={{ 
                    backgroundColor: '#f3f4f6', 
                    borderBottom: '1px solid #e5e7eb',
                    cursor: 'pointer'
                  }}
                  onClick={() => setExpandedSections({ ...expandedSections, minerals: !expandedSections.minerals })}
                >
                  <td colSpan={5} style={{ padding: '10px 12px', fontWeight: 600, fontSize: '0.9rem', position: 'sticky', left: 0, backgroundColor: '#f3f4f6' }}>
                    <span style={{ marginRight: '8px' }}>{expandedSections.minerals ? '▼' : '▶'}</span>
                    Minerals
                  </td>
                </tr>
                {expandedSections.minerals && selectedComparison.target.minerals.map(min => {
                  const actualValue = (selectedComparison.actual as any)?.[min.key] || 0;
                  const diff = selectedComparison.differences.minerals[min.key];
                  return (
                    <tr key={min.key} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '10px 12px 10px 32px', fontWeight: 500, position: 'sticky', left: 0, backgroundColor: 'white' }}>
                        {min.label}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>{min.value.toFixed(1)} {min.unit}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>
                        {actualValue.toFixed(1)} {min.unit}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: getStatusColor(diff), fontWeight: 600 }}>
                        {diff !== null ? `${diff > 0 ? '+' : ''}${diff.toFixed(1)} ${min.unit}` : '-'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <span style={{ color: getStatusColor(diff), fontWeight: 600, fontSize: '0.85rem' }}>
                          {getStatusLabel(diff)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Daily Meal Plan and Recipes vs Logged Meals Comparison */}
          {selectedComparison && activeMealPlan && mealPlanVersion && (() => {
            // Find daily meal plan and recipes day for selected date
            const selectedDateObj = new Date(selectedComparison.date);
            const mealPlanDay = mealPlanVersion.days.find(day => {
              const dayDate = new Date(day.date);
              return dayDate.toDateString() === selectedDateObj.toDateString();
            });

            if (!mealPlanDay || mealPlanDay.items.length === 0) return null;

            // Group daily meal plan and recipes items by meal type
            const mealPlanByMealType = mealPlanDay.items.reduce((acc, item) => {
              const mealType = item.meal_key.toLowerCase();
              if (!acc[mealType]) {
                acc[mealType] = [];
              }
              acc[mealType].push(item);
              return acc;
            }, {} as Record<string, typeof mealPlanDay.items>);

            // Group logged meals by meal type (already done above, but need it here)
            const loggedMealsByType = selectedComparison.loggedMeals.reduce((acc, meal) => {
              const mealType = meal.meal_type.toLowerCase();
              if (!acc[mealType]) {
                acc[mealType] = [];
              }
              acc[mealType].push(meal);
              return acc;
            }, {} as Record<string, NutritionDataResponse[]>);

            // Get all meal types (from both daily meal plan and recipes and logged meals)
            const allMealTypes = new Set([
              ...Object.keys(mealPlanByMealType),
              ...Object.keys(loggedMealsByType)
            ]);

            return (
              <div style={{ marginBottom: '24px' }}>
                {/* Collapsible Header */}
                <div
                  onClick={() => setExpandedMealPlanComparison(!expandedMealPlanComparison)}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px 8px 0 0',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e5e7eb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }}
                >
                  <span style={{ fontSize: '0.85rem', color: '#6b7280', width: '16px', display: 'inline-block' }}>
                    {expandedMealPlanComparison ? '▼' : '▶'}
                  </span>
                  <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '600', color: '#374151' }}>
                    Daily Meal Plan and Recipes vs Logged Meals Comparison
                  </h4>
                </div>

                {expandedMealPlanComparison && (
                  <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#ffffff' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', position: 'sticky', left: 0, backgroundColor: '#f9fafb', zIndex: 10 }}>Meal Type</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem' }}>Daily Meal Plan and Recipes</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem' }}>Logged Meals</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, fontSize: '0.875rem' }}>Plan Calories</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, fontSize: '0.875rem' }}>Logged Calories</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600, fontSize: '0.875rem' }}>Difference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from(allMealTypes).sort((a, b) => {
                        const order = ['breakfast', 'lunch', 'dinner', 'snack'];
                        const aIdx = order.indexOf(a);
                        const bIdx = order.indexOf(b);
                        if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
                        if (aIdx === -1) return 1;
                        if (bIdx === -1) return -1;
                        return aIdx - bIdx;
                      }).map((mealType) => {
                        const planItems = mealPlanByMealType[mealType] || [];
                        const loggedMeals = loggedMealsByType[mealType] || [];

                        // Calculate totals
                        const planCalories = planItems.reduce((sum, item) => {
                          return sum + (item.dish_nutrients?.values?.calories || 0);
                        }, 0);
                        const loggedCalories = loggedMeals.reduce((sum, meal) => sum + (meal.calories || 0), 0);
                        const diff = loggedCalories - planCalories;

                        return (
                          <tr key={mealType} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '10px 12px', fontWeight: 600, textTransform: 'capitalize', position: 'sticky', left: 0, backgroundColor: 'white' }}>
                              {mealType}
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              {planItems.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  {planItems.map((item, idx) => (
                                    <div key={idx} style={{ fontSize: '0.85rem' }}>
                                      {item.dish_name}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>-</span>
                              )}
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              {loggedMeals.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  {loggedMeals.map((meal, idx) => (
                                    <div key={idx} style={{ fontSize: '0.85rem' }}>
                                      {meal.dish_name || meal.food_item_name || 'Unnamed Meal'}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>-</span>
                              )}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: planItems.length > 0 ? '#374151' : '#9ca3af' }}>
                              {planItems.length > 0 ? `${Math.round(planCalories)} kcal` : '-'}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: loggedMeals.length > 0 ? '#374151' : '#9ca3af', fontWeight: 600 }}>
                              {loggedMeals.length > 0 ? `${Math.round(loggedCalories)} kcal` : '-'}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: planItems.length > 0 && loggedMeals.length > 0 ? getStatusColor(diff) : '#9ca3af', fontWeight: 600 }}>
                              {planItems.length > 0 && loggedMeals.length > 0 ? (
                                `${diff > 0 ? '+' : ''}${Math.round(diff)} kcal`
                              ) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Logged Meals for Selected Date - Grouped by Meal Type */}
          {selectedComparison.loggedMeals.length > 0 && (() => {
            // Group meals by meal_type
            const mealsByType = selectedComparison.loggedMeals.reduce((acc, meal) => {
              const mealType = meal.meal_type.toLowerCase();
              if (!acc[mealType]) {
                acc[mealType] = [];
              }
              acc[mealType].push(meal);
              return acc;
            }, {} as Record<string, NutritionDataResponse[]>);

            // Calculate totals for each meal type
            const mealTypeTotals = Object.entries(mealsByType).map(([mealType, meals]) => {
              const totals = meals.reduce((acc, meal) => ({
                calories: acc.calories + (meal.calories || 0),
                protein_g: acc.protein_g + (meal.protein_g || 0),
                carbs_g: acc.carbs_g + (meal.carbs_g || 0),
                fat_g: acc.fat_g + (meal.fat_g || 0),
                fiber_g: acc.fiber_g + (meal.fiber_g || 0),
              }), { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0 });

              return { mealType, meals, totals };
            });

            mealTypeTotals.sort((a, b) => {
              const aIdx = DEFAULT_MEAL_TYPE_ORDER.indexOf(a.mealType as any);
              const bIdx = DEFAULT_MEAL_TYPE_ORDER.indexOf(b.mealType as any);
              if (aIdx === -1 && bIdx === -1) return a.mealType.localeCompare(b.mealType);
              if (aIdx === -1) return 1;
              if (bIdx === -1) return -1;
              return aIdx - bIdx;
            });

            return (
              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem', fontWeight: '600', color: '#374151' }}>
                  Logged Meals ({selectedComparison.loggedMeals.length})
                </h4>
                <div style={{ display: 'grid', gap: '16px' }}>
                  {mealTypeTotals.map(({ mealType, meals, totals }) => (
                    <div
                      key={mealType}
                      style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Meal Type Header with Totals - Collapsible */}
                      <div
                        onClick={() => setExpandedMealTypes({ ...expandedMealTypes, [mealType]: !expandedMealTypes[mealType] })}
                        style={{
                          padding: '12px 16px',
                          backgroundColor: '#f3f4f6',
                          borderBottom: '1px solid #e5e7eb',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#e5e7eb';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#f3f4f6';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '0.85rem', color: '#6b7280', width: '16px', display: 'inline-block' }}>
                            {expandedMealTypes[mealType] === true ? '▼' : '▶'}
                          </span>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.95rem', textTransform: 'capitalize', marginBottom: '4px' }}>
                              {mealType}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                              {meals.length} {meals.length === 1 ? 'meal' : 'meals'}
                            </div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                            {Math.round(totals.calories)} kcal
                          </div>
                          <div style={{ display: 'flex', gap: '12px', fontSize: '0.8rem', color: '#6b7280' }}>
                            <span>P: {totals.protein_g.toFixed(1)}g</span>
                            <span>C: {totals.carbs_g.toFixed(1)}g</span>
                            <span>F: {totals.fat_g.toFixed(1)}g</span>
                            {totals.fiber_g > 0 && <span>Fiber: {totals.fiber_g.toFixed(1)}g</span>}
                          </div>
                        </div>
                      </div>

                      {/* Individual Meals - Collapsible Table */}
                      {expandedMealTypes[mealType] === true && (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                            <thead>
                              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: '0.8rem', color: '#6b7280' }}>Meal Name</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, fontSize: '0.8rem', color: '#6b7280' }}>Time</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, fontSize: '0.8rem', color: '#6b7280' }}>Calories</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, fontSize: '0.8rem', color: '#6b7280' }}>Protein</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, fontSize: '0.8rem', color: '#6b7280' }}>Carbs</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, fontSize: '0.8rem', color: '#6b7280' }}>Fat</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, fontSize: '0.8rem', color: '#6b7280' }}>Fiber</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: '0.8rem', color: '#6b7280' }}>Notes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {meals.map((meal, idx) => (
                                <tr 
                                  key={idx}
                                  style={{ 
                                    borderBottom: idx < meals.length - 1 ? '1px solid #e5e7eb' : 'none',
                                    backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb'
                                  }}
                                >
                                  <td style={{ padding: '10px 12px', fontWeight: 500 }}>
                                    {meal.dish_name || meal.food_item_name || 'Unnamed Meal'}
                                  </td>
                                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#6b7280', fontSize: '0.85rem' }}>
                                    {new Date(meal.meal_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                  </td>
                                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>
                                    {Math.round(meal.calories)} kcal
                                  </td>
                                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#6b7280' }}>
                                    {meal.protein_g.toFixed(1)}g
                                  </td>
                                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#6b7280' }}>
                                    {meal.carbs_g.toFixed(1)}g
                                  </td>
                                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#6b7280' }}>
                                    {meal.fat_g.toFixed(1)}g
                                  </td>
                                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#6b7280' }}>
                                    {meal.fiber_g > 0 ? `${meal.fiber_g.toFixed(1)}g` : '-'}
                                  </td>
                                  <td style={{ padding: '10px 12px', color: '#6b7280', fontSize: '0.85rem', fontStyle: meal.notes ? 'italic' : 'normal' }}>
                                    {meal.notes || '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {selectedComparison.loggedMeals.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '0.9rem' }}>
              No meals logged for this date.
            </div>
          )}
            </>
          )}
        </div>
      )}

      {dailyComparisons.length === 0 && (
        <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', fontSize: '0.9rem' }}>
          No nutrition data available for the selected date range.
        </div>
      )}
    </div>
  );
};

export default MealPlanMonitoringTab;

